import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import {
  useCreateCustomProjectMutation,
  useGetGeoCitiesQuery,
} from '@/api/customProjects/customProjectsApi';
import type { GeoCity } from '@/api/customProjects/types';

import { Button } from '@/components/atoms/Button';
import { Popup } from '@/components/atoms/Popup';
import { getErrorMessage, useToast } from '@/components/atoms/Toast';

const inputSx = {
  '& .MuiOutlinedInput-root': {
    height: 36,
    fontSize: 12,
    fontWeight: 500,
    color: '#1e2a39',
    borderRadius: '6px',
    '& fieldset': {
      borderColor: 'rgba(74, 85, 101, 0.3)',
      borderWidth: '0.6px',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(74, 85, 101, 0.45)',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'rgba(74, 85, 101, 0.55)',
    },
  },
  '& .MuiInputBase-input': {
    py: 1,
  },
} as const;

function SelectDropdownIcon(props: SvgIconProps) {
  const { sx: sxProp, ...rest } = props;
  const extra = Array.isArray(sxProp) ? sxProp : sxProp != null ? [sxProp] : [];
  return <KeyboardArrowDownIcon {...rest} sx={[{ fontSize: 18, color: '#1e2a39' }, ...extra]} />;
}

function FieldLabel({ children, required }: { children: ReactNode; required?: boolean }) {
  return (
    <Typography
      component="span"
      sx={{
        fontSize: 11,
        lineHeight: '14px',
        color: '#1e2a39',
        display: 'block',
        mb: 0.75,
      }}
    >
      {children}
      {required ? (
        <Box component="span" sx={{ color: '#fb2c36' }}>
          *
        </Box>
      ) : null}
    </Typography>
  );
}

const MAX_NAME_OR_AREA_LENGTH = 250;

const emptyForm = {
  projectName: '',
  clientName: '',
  city: '',
  state: '',
  projectArea: '',
  projectAddress: '',
  additionalInformation: '',
};

export type CreateNewProjectDialogProps = {
  open: boolean;
  onClose: () => void;
};

export function CreateNewProjectDialog({ open, onClose }: CreateNewProjectDialogProps) {
  const toast = useToast();
  const [form, setForm] = useState(emptyForm);
  const [touched, setTouched] = useState(false);

  const [createProject, { isLoading }] = useCreateCustomProjectMutation();
  const {
    data: geoCities = [],
    isError: geoCitiesError,
    error: geoCitiesQueryError,
  } = useGetGeoCitiesQuery(undefined, {
    skip: !open,
  });

  useEffect(() => {
    if (open) {
      setForm(emptyForm);
      setTouched(false);
    }
  }, [open]);

  useEffect(() => {
    if (!geoCitiesError) return;
    toast.error(getErrorMessage(geoCitiesQueryError) || 'Failed to load city options');
  }, [geoCitiesError, geoCitiesQueryError, toast]);

  const sortedGeoCities = useMemo(
    () =>
      [...geoCities].sort((a: GeoCity, b: GeoCity) =>
        (a?.name || '').toUpperCase().localeCompare((b?.name || '').toUpperCase()),
      ),
    [geoCities],
  );

  const stateOptions = useMemo(
    () =>
      sortedGeoCities
        .reduce<string[]>((prev, curr) => {
          if (curr?.state && !prev.includes(curr.state)) prev.push(curr.state);
          return prev;
        }, [])
        .filter(Boolean)
        .sort((a, b) => a.toUpperCase().localeCompare(b.toUpperCase())),
    [sortedGeoCities],
  );

  const cityOptions = useMemo(() => {
    const filteredCities = sortedGeoCities.filter(city => !form.state || city.state === form.state);
    const options = filteredCities.map(city => ({
      label: city.name,
      state: city.state,
      value: city.name,
    }));

    return [...options, { label: 'Other', value: 'Other', state: form.state }];
  }, [form.state, sortedGeoCities]);

  const updateField = (key: keyof typeof emptyForm) => (value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const requiredError = useCallback((value: string) => touched && !value.trim(), [touched]);

  const handleSubmit = useCallback(async () => {
    setTouched(true);
    const { projectName, clientName, city, state, projectArea, projectAddress } = form;
    if (
      !projectName.trim() ||
      !clientName.trim() ||
      !city ||
      !state ||
      !projectArea.trim() ||
      !projectAddress.trim()
    ) {
      return;
    }

    try {
      await createProject({
        location: {
          city,
          state,
          address: projectAddress.trim(),
        },
        metaData: {
          areaSft: projectArea.trim(),
          additionalInformation: form.additionalInformation.trim() || '',
        },
        projectName: projectName.trim(),
        clientName: clientName.trim(),
      }).unwrap();
      toast.success('Project created');
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }, [createProject, form, onClose, toast]);

  return (
    <Popup
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title={
        <Stack spacing={0.5}>
          <Typography
            component="span"
            sx={{ fontSize: 16, fontWeight: 600, lineHeight: '18px', color: '#1e2a39' }}
          >
            Create New Project
          </Typography>
          <Typography
            component="span"
            sx={{ fontSize: 12, fontWeight: 400, lineHeight: '20px', color: '#4a5565' }}
          >
            Enter project details to get started
          </Typography>
        </Stack>
      }
      actions={
        <Button
          fullWidth
          disabled={isLoading}
          onClick={handleSubmit}
          sx={{
            height: 36,
            borderRadius: '6px',
            fontSize: 12,
            fontWeight: 500,
          }}
        >
          Create Project
        </Button>
      }
    >
      <Stack spacing={2.25}>
        <Box>
          <FieldLabel required>Project Name</FieldLabel>
          <TextField
            fullWidth
            size="small"
            placeholder="e.g. Modern Office Renovation"
            value={form.projectName}
            onChange={e =>
              updateField('projectName')(e.target.value.slice(0, MAX_NAME_OR_AREA_LENGTH))
            }
            inputProps={{ maxLength: MAX_NAME_OR_AREA_LENGTH }}
            error={requiredError(form.projectName)}
            helperText={requiredError(form.projectName) ? 'Required' : undefined}
            sx={inputSx}
          />
        </Box>

        <Box>
          <FieldLabel required>Client Name</FieldLabel>
          <TextField
            fullWidth
            size="small"
            placeholder="e.g. Space Ventures"
            value={form.clientName}
            onChange={e =>
              updateField('clientName')(e.target.value.slice(0, MAX_NAME_OR_AREA_LENGTH))
            }
            inputProps={{ maxLength: MAX_NAME_OR_AREA_LENGTH }}
            error={requiredError(form.clientName)}
            helperText={requiredError(form.clientName) ? 'Required' : undefined}
            sx={inputSx}
          />
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <FieldLabel required>State</FieldLabel>
            <FormControl fullWidth size="small" error={touched && !form.state}>
              <Select
                displayEmpty
                value={form.state}
                onChange={e =>
                  setForm(prev => ({
                    ...prev,
                    state: e.target.value,
                    city:
                      prev.city === 'Other' ||
                      cityOptions.some(
                        option => option.value === prev.city && option.state === e.target.value,
                      )
                        ? prev.city
                        : '',
                  }))
                }
                IconComponent={SelectDropdownIcon}
                sx={{
                  height: 36,
                  fontSize: 12,
                  fontWeight: 500,
                  color: '#1e2a39',
                  borderRadius: '6px',
                  '& fieldset': {
                    borderColor: 'rgba(74, 85, 101, 0.3)',
                    borderWidth: '0.6px',
                  },
                }}
              >
                <MenuItem value="" disabled sx={{ fontSize: 12 }}>
                  Select state
                </MenuItem>
                {stateOptions.map(option => (
                  <MenuItem key={option} value={option} sx={{ fontSize: 12 }}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
              {touched && !form.state ? (
                <FormHelperText error sx={{ mx: 0, mt: 0.5 }}>
                  Required
                </FormHelperText>
              ) : null}
            </FormControl>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <FieldLabel required>City</FieldLabel>
            <FormControl fullWidth size="small" error={touched && !form.city}>
              <Select
                displayEmpty
                value={form.city}
                onChange={e => {
                  const selectedCity = cityOptions.find(option => option.value === e.target.value);
                  setForm(prev => ({
                    ...prev,
                    city: e.target.value,
                    state: selectedCity?.state || prev.state,
                  }));
                }}
                IconComponent={SelectDropdownIcon}
                disabled={!cityOptions.length}
                sx={{
                  height: 36,
                  fontSize: 12,
                  fontWeight: 500,
                  color: '#1e2a39',
                  borderRadius: '6px',
                  '& fieldset': {
                    borderColor: 'rgba(74, 85, 101, 0.3)',
                    borderWidth: '0.6px',
                  },
                }}
              >
                <MenuItem value="" disabled sx={{ fontSize: 12 }}>
                  Select city
                </MenuItem>
                {cityOptions.map(option => (
                  <MenuItem
                    key={`${option.value}-${option.state || 'na'}`}
                    value={option.value}
                    sx={{ fontSize: 12 }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {touched && !form.city ? (
                <FormHelperText error sx={{ mx: 0, mt: 0.5 }}>
                  Required
                </FormHelperText>
              ) : null}
            </FormControl>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <FieldLabel required>Project Area</FieldLabel>
            <TextField
              fullWidth
              size="small"
              value={form.projectArea}
              onChange={e =>
                updateField('projectArea')(e.target.value.slice(0, MAX_NAME_OR_AREA_LENGTH))
              }
              inputProps={{ maxLength: MAX_NAME_OR_AREA_LENGTH }}
              error={requiredError(form.projectArea)}
              helperText={requiredError(form.projectArea) ? 'Required' : undefined}
              sx={inputSx}
            />
          </Box>
        </Stack>

        <Box>
          <FieldLabel required>Project Address</FieldLabel>
          <TextField
            fullWidth
            size="small"
            placeholder="Street, building, area"
            value={form.projectAddress}
            onChange={e => updateField('projectAddress')(e.target.value)}
            error={requiredError(form.projectAddress)}
            helperText={requiredError(form.projectAddress) ? 'Required' : undefined}
            sx={{
              ...inputSx,
              '& .MuiOutlinedInput-root': {
                ...inputSx['& .MuiOutlinedInput-root'],
                height: 'auto',
                minHeight: 38,
                alignItems: 'flex-start',
              },
            }}
            multiline
            minRows={2}
          />
        </Box>

        <Box>
          <FieldLabel>Additional Information</FieldLabel>
          <TextField
            fullWidth
            size="small"
            placeholder="Optional notes"
            value={form.additionalInformation}
            onChange={e => updateField('additionalInformation')(e.target.value)}
            multiline
            minRows={3}
            sx={{
              ...inputSx,
              '& .MuiOutlinedInput-root': {
                ...inputSx['& .MuiOutlinedInput-root'],
                height: 'auto',
                minHeight: 67,
                alignItems: 'flex-start',
                pt: 1,
                pb: 1,
              },
            }}
          />
        </Box>
      </Stack>
    </Popup>
  );
}
